/**
 * Daily quests & streak (v1) — stored in user_progress.daily_quests / progress.daily_quests
 */

export const STREAK_FREEZE_COST = 100;
export const STREAK_QUESTS_REQUIRED = 3;
export const STREAK_SECTOR_COUNT = 3;
export const STREAK_MANUAL_QUESTS_REQUIRED = 2;
export const STREAK_GREEN = '#22c55e';
export const STREAK_GREEN_DARK = '#15803d';
export const STREAK_GREY = '#e2e8f0';
export const PATH_LENGTH = 28;
export const PRIZE_DAYS = [7, 14, 28];

const PRIZE_META = {
    7: { icon: '🎁', label: 'Week reward', defaultCoins: 10 },
    14: { icon: '💎', label: '2-week reward', defaultCoins: 25 },
    28: { icon: '👑', label: 'Month reward', defaultCoins: 50 },
};

/** @deprecated Use getPrizeRewardsFromRules — kept for static icon/label defaults */
export const PRIZE_REWARDS = Object.fromEntries(
    PRIZE_DAYS.map((day) => [
        day,
        { ...PRIZE_META[day], coins: PRIZE_META[day].defaultCoins },
    ])
);

const STREAK_PRIZE_RULE_KEYS = {
    7: 'streakPrize7Coins',
    14: 'streakPrize14Coins',
    28: 'streakPrize28Coins',
};

export function getPrizeRewardsFromRules(automationRules = {}) {
    return Object.fromEntries(
        PRIZE_DAYS.map((day) => {
            const meta = PRIZE_META[day];
            const ruleKey = STREAK_PRIZE_RULE_KEYS[day];
            const raw = automationRules[ruleKey];
            const coins = raw === undefined || raw === null || raw === ''
                ? meta.defaultCoins
                : Math.max(0, Number(raw) || 0);
            return [day, { icon: meta.icon, label: meta.label, coins }];
        })
    );
}

export const PAID_QUEST_IDS = ['paid_surf', 'paid_challenge', 'paid_dictation', 'paid_sentences'];

/** Max quests shown on Home per calendar day. */
export const DAILY_QUEST_SLOT_COUNT = 4;

/** Looking Back + paid practice — rotated by weekday; skipped when unavailable (policy A). */
export const WEEKDAY_FILLER_POOL = [
    'looking_back',
    'paid_surf',
    'paid_challenge',
    'paid_dictation',
    'paid_sentences',
];

/** Offline / honor-system fillers — only used when weekday fillers cannot fill remaining slots. */
export const UNIVERSAL_FILLER_IDS = ['listen_song', 'watch_media', 'read_news'];

const PAID_MODE_TO_QUEST = {
    SURF: 'paid_surf',
    CHALLENGE: 'paid_challenge',
    DICTATION: 'paid_dictation',
    SENTENCES: 'paid_sentences',
};

export const DAILY_QUEST_DEFS = [
    { id: 'open_app', icon: '🌿', label: 'Open the App', hint: 'Counts when you visit today', coins: 0, color: '#10b981', autoComplete: true },
    { id: 'training', icon: '📚', label: 'Use Training Modes', hint: 'Meet, Study, Spelling, or Review', coins: 1, color: '#4361ee' },
    { id: 'looking_back', icon: '🎴', label: 'Use Looking Back', hint: 'Cards or Spell (10+ mastered words)', coins: 1, color: '#f72585' },
    { id: 'paid_surf', icon: '🏄', label: 'Surf', hint: 'Start a paid Surf session', coins: 1, color: '#ff9f1c' },
    { id: 'paid_challenge', icon: '🎯', label: 'Challenge', hint: 'Start a 10-word Challenge', coins: 1, color: '#ff9f1c' },
    { id: 'paid_dictation', icon: '🎧', label: 'Dictation', hint: 'Start a Dictation session', coins: 1, color: '#ff9f1c' },
    { id: 'paid_sentences', icon: '📝', label: 'Sentences', hint: 'Start a Sentences session', coins: 1, color: '#ff9f1c' },
    { id: 'exercise', icon: '📝', label: 'Do an exercise', hint: 'Finish & send an assigned exercise', coins: 1, color: '#0ea5e9' },
    {
        id: 'listen_song',
        icon: '🎵',
        label: 'Listen to a song in English and read its lyrics',
        hint: 'Tap to mark done when finished',
        coins: 1,
        color: '#8b5cf6',
        selfReport: true,
    },
    {
        id: 'watch_media',
        icon: '🎬',
        label: 'Watch an episode of a series or a movie in English',
        hint: 'Tap to mark done when finished',
        coins: 1,
        color: '#8b5cf6',
        selfReport: true,
    },
    {
        id: 'read_news',
        icon: '📰',
        label: 'Read some news in English',
        hint: 'Tap to mark done when finished',
        coins: 1,
        color: '#8b5cf6',
        selfReport: true,
    },
];

/** Automation rule keys for per-quest coin prizes (Rewards → Automations). */
export const DAILY_QUEST_COIN_RULE_KEYS = {
    training: 'dailyQuestTrainingCoins',
    looking_back: 'dailyQuestLookingBackCoins',
    paid_surf: 'dailyQuestPaidSurfCoins',
    paid_challenge: 'dailyQuestPaidChallengeCoins',
    paid_dictation: 'dailyQuestPaidDictationCoins',
    paid_sentences: 'dailyQuestPaidSentencesCoins',
    exercise: 'dailyQuestExerciseCoins',
    listen_song: 'dailyQuestListenSongCoins',
    watch_media: 'dailyQuestWatchMediaCoins',
    read_news: 'dailyQuestReadNewsCoins',
};

/** Resolve coin prize for a daily quest from automation rules (falls back to def.coins). */
export function getQuestCoinsFromRules(questId, automationRules = {}) {
    const def = DAILY_QUEST_DEFS.find((q) => q.id === questId);
    if (!def) return 0;
    const ruleKey = DAILY_QUEST_COIN_RULE_KEYS[questId];
    if (!ruleKey) return Math.max(0, Number(def.coins) || 0);
    const raw = automationRules[ruleKey];
    if (raw === undefined || raw === null || raw === '') {
        return Math.max(0, Number(def.coins) || 0);
    }
    return Math.max(0, Number(raw) || 0);
}

/** Where tapping a daily quest row should take the student (tab + scroll target). */
export const QUEST_NAV_TARGETS = {
    open_app: { tab: 'HOME', sectionId: 'student-home-top' },
    training: { tab: 'WORDS', sectionId: 'student-words-training' },
    looking_back: { tab: 'WORDS', sectionId: 'student-words-looking-back' },
    paid_surf: { tab: 'WORDS', sectionId: 'student-words-paid-practice' },
    paid_challenge: { tab: 'WORDS', sectionId: 'student-words-paid-practice' },
    paid_dictation: { tab: 'WORDS', sectionId: 'student-words-paid-practice' },
    paid_sentences: { tab: 'WORDS', sectionId: 'student-words-paid-practice' },
    exercise: { tab: 'HOME', sectionId: 'student-home-exercises' },
};

const TRAINING_MODES = new Set(['MEET', 'STUDY', 'TEST', 'REVIEW']);
const LOOKING_BACK_MODES = new Set(['CARDS', 'SPELL', 'PREPOSITIONS', 'AUDIO']);
const PAID_MODES = new Set(Object.keys(PAID_MODE_TO_QUEST));

export function getTodayDateStr(date = new Date()) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function addDays(dateStr, delta) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + delta);
    return getTodayDateStr(dt);
}

export function createEmptyDailyQuestState(today = getTodayDateStr()) {
    return {
        streak_days: 0,
        today_date: today,
        last_marked_date: null,
        day_marked: false,
        tasks_done: {},
        quest_coins_awarded: [],
        streak_freeze_armed: false,
        streak_freeze_armed_on: null,
        streak_prizes_claimed: [],
        /** YYYY-MM-DD list of days when all required daily tasks were completed */
        marked_dates: [],
    };
}

function migrateLegacyPaidPracticeQuest(state) {
    let tasks_done = state.tasks_done && typeof state.tasks_done === 'object'
        ? { ...state.tasks_done }
        : {};
    let quest_coins_awarded = Array.isArray(state.quest_coins_awarded)
        ? [...state.quest_coins_awarded]
        : [];

    if (tasks_done.paid_practice) {
        const ts = tasks_done.paid_practice;
        for (const id of PAID_QUEST_IDS) {
            if (!tasks_done[id]) tasks_done[id] = ts;
        }
        delete tasks_done.paid_practice;
    }

    if (quest_coins_awarded.includes('paid_practice')) {
        quest_coins_awarded = quest_coins_awarded.filter((id) => id !== 'paid_practice');
        for (const id of PAID_QUEST_IDS) {
            if (!quest_coins_awarded.includes(id)) quest_coins_awarded.push(id);
        }
    }

    return { ...state, tasks_done, quest_coins_awarded };
}

export function normalizeDailyQuestState(raw) {
    if (!raw || typeof raw !== 'object') return createEmptyDailyQuestState();
    const markedFromRaw = Array.isArray(raw.marked_dates)
        ? raw.marked_dates.filter((d) => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)).map((d) => d.slice(0, 10))
        : [];
    // Seed history from known fields so older accounts still show past completions
    const seed = new Set(markedFromRaw);
    if (raw.last_marked_date && /^\d{4}-\d{2}-\d{2}/.test(String(raw.last_marked_date))) {
        seed.add(String(raw.last_marked_date).slice(0, 10));
    }
    if (raw.day_marked && typeof raw.today_date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw.today_date)) {
        seed.add(raw.today_date.slice(0, 10));
    }
    const base = {
        streak_days: Math.max(0, Number(raw.streak_days) || 0),
        today_date: typeof raw.today_date === 'string' ? raw.today_date : getTodayDateStr(),
        last_marked_date: raw.last_marked_date || null,
        day_marked: !!raw.day_marked,
        tasks_done: raw.tasks_done && typeof raw.tasks_done === 'object' ? { ...raw.tasks_done } : {},
        quest_coins_awarded: Array.isArray(raw.quest_coins_awarded) ? [...raw.quest_coins_awarded] : [],
        streak_freeze_armed: !!raw.streak_freeze_armed,
        streak_freeze_armed_on: raw.streak_freeze_armed_on || null,
        streak_prizes_claimed: Array.isArray(raw.streak_prizes_claimed)
            ? [...new Set(raw.streak_prizes_claimed.map((d) => Number(d)).filter((d) => PRIZE_DAYS.includes(d)))]
            : [],
        marked_dates: [...seed].sort(),
    };
    return migrateLegacyPaidPracticeQuest(base);
}

export function countQuestsDone(state) {
    return DAILY_QUEST_DEFS.filter((q) => state.tasks_done?.[q.id]).length;
}

export function isDayMarked(state) {
    return !!state.day_marked || countQuestsDone(state) >= STREAK_QUESTS_REQUIRED;
}

/** New calendar day: apply streak rules and reset today's tasks */
export function rolloverDailyQuestState(state, today = getTodayDateStr()) {
    const s = normalizeDailyQuestState(state);
    if (s.today_date === today) return s;

    const prevDate = s.today_date;
    const prevMarked = isDayMarked(s);

    let streak_days = s.streak_days;
    let last_marked_date = s.last_marked_date;
    let streak_freeze_armed = s.streak_freeze_armed;
    let streak_freeze_armed_on = s.streak_freeze_armed_on;
    const markedSet = new Set(s.marked_dates || []);

    if (prevDate) {
        if (prevMarked) {
            streak_days = Math.max(streak_days, 0) + 1;
            last_marked_date = prevDate;
            markedSet.add(prevDate);
        } else if (streak_freeze_armed) {
            streak_freeze_armed = false;
            streak_freeze_armed_on = null;
        } else {
            streak_days = 0;
            last_marked_date = null;
        }
    }

    const streakReset = streak_days === 0 && s.streak_days > 0;

    return {
        streak_days,
        today_date: today,
        last_marked_date,
        day_marked: false,
        tasks_done: {},
        quest_coins_awarded: [],
        streak_freeze_armed,
        streak_freeze_armed_on,
        streak_prizes_claimed: streakReset ? [] : (s.streak_prizes_claimed || []),
        marked_dates: [...markedSet].sort(),
    };
}

export function extractDailyQuestState(progress) {
    const blob = progress?.progress || {};
    const raw = progress?.daily_quests ?? blob?.daily_quests ?? null;
    const today = getTodayDateStr();
    return rolloverDailyQuestState(normalizeDailyQuestState(raw), today);
}

/**
 * Grant streak-path milestone coins when streak_days reaches 7 / 14 / 28 (once per milestone per streak run).
 * @returns {{ state: object, awards: Array<{ day: number, coins: number, label: string }>, changed: boolean }}
 */
export function applyStreakPrizeMilestones(state, automationRules = {}) {
    const s = normalizeDailyQuestState(state);
    const prizes = getPrizeRewardsFromRules(automationRules);
    const claimed = new Set(s.streak_prizes_claimed || []);
    const awards = [];

    for (const day of PRIZE_DAYS) {
        if (s.streak_days >= day && !claimed.has(day)) {
            const coins = Math.max(0, Number(prizes[day]?.coins) || 0);
            if (coins > 0) {
                awards.push({ day, coins, label: prizes[day].label });
            }
            claimed.add(day);
        }
    }

    const nextClaimed = [...claimed].sort((a, b) => a - b);
    const prevClaimed = s.streak_prizes_claimed || [];
    const changed =
        awards.length > 0
        || nextClaimed.length !== prevClaimed.length
        || nextClaimed.some((d, i) => d !== prevClaimed[i]);

    if (!changed) {
        return { state: s, awards: [], changed: false };
    }

    return {
        state: { ...s, streak_prizes_claimed: nextClaimed },
        awards,
        changed: true,
    };
}

export function dailyQuestStateNeedsPersist(persisted, synced) {
    const a = normalizeDailyQuestState(persisted);
    const b = normalizeDailyQuestState(synced);
    return (
        a.today_date !== b.today_date
        || a.streak_days !== b.streak_days
        || a.day_marked !== b.day_marked
        || JSON.stringify(a.tasks_done) !== JSON.stringify(b.tasks_done)
        || JSON.stringify(a.quest_coins_awarded) !== JSON.stringify(b.quest_coins_awarded)
        || JSON.stringify([...(a.streak_prizes_claimed || [])].sort((x, y) => x - y))
            !== JSON.stringify([...(b.streak_prizes_claimed || [])].sort((x, y) => x - y))
        || JSON.stringify(a.marked_dates || []) !== JSON.stringify(b.marked_dates || [])
        || a.streak_freeze_armed !== b.streak_freeze_armed
        || a.streak_freeze_armed_on !== b.streak_freeze_armed_on
    );
}

export function getQuestAvailability({
    stats = {},
    readyForRevision = 0,
    coins = 0,
    automationRules = {},
    pendingExerciseCount = 0,
    paidPracticePoolCount = 0,
    sentencesPracticePoolCount = 0,
}) {
    const learnt = (stats['LEARNT'] || 0) + (stats['REVISED'] || 0);
    const surfCost = Number(automationRules.paidSurfEntryCoins ?? 10);
    const challengeCost = Number(automationRules.paidChallengeEntryCoins ?? 10);
    const dictationCost = Number(automationRules.paidDictationEntryCoins ?? 10);
    const sentencesCost = Number(automationRules.paidSentencesEntryCoins ?? 10);
    const hasPaidPool = paidPracticePoolCount >= 10;
    const hasSentencesPool = sentencesPracticePoolCount >= 10;

    const hasTrainingWords =
        (stats['NOT STARTED'] || 0) > 0
        || (stats['LEARNING'] || 0) > 0
        || (stats['FOR TEST'] || 0) > 0
        || (stats['FOR REVISION'] || 0) > 0
        || readyForRevision > 0;

    return {
        open_app: true,
        training: hasTrainingWords,
        looking_back: learnt >= 10,
        paid_surf: surfCost > 0 && coins >= surfCost && hasPaidPool,
        paid_challenge: challengeCost > 0 && coins >= challengeCost && hasPaidPool,
        paid_dictation: dictationCost > 0 && coins >= dictationCost && hasPaidPool,
        paid_sentences: sentencesCost > 0 && coins >= sentencesCost && hasSentencesPool,
        exercise: pendingExerciseCount > 0,
        listen_song: true,
        watch_media: true,
        read_news: true,
    };
}

/** Weekday-rotated order of Looking Back + paid fillers (Sun=0 … Sat=6). */
export function weekdayFillerOrder(date = new Date()) {
    const start = date.getDay() % WEEKDAY_FILLER_POOL.length;
    return [
        ...WEEKDAY_FILLER_POOL.slice(start),
        ...WEEKDAY_FILLER_POOL.slice(0, start),
    ];
}

/**
 * Build up to DAILY_QUEST_SLOT_COUNT quest ids for today:
 * 1) open_app always
 * 2) training / exercise only when available (do not reserve empty slots);
 *    keep them if already completed today so the checkmark stays visible
 * 3) weekday fillers that are available (skip locked — policy A)
 * 4) universal offline fillers if still short
 */
export function getTodayQuestSlotIds(availability = {}, date = new Date(), state = null) {
    const done = state?.tasks_done || {};
    const ids = ['open_app'];
    if (availability.training || done.training) ids.push('training');
    if (availability.exercise || done.exercise) ids.push('exercise');

    for (const id of weekdayFillerOrder(date)) {
        if (ids.length >= DAILY_QUEST_SLOT_COUNT) break;
        if (availability[id] || done[id]) ids.push(id);
    }
    for (const id of UNIVERSAL_FILLER_IDS) {
        if (ids.length >= DAILY_QUEST_SLOT_COUNT) break;
        if (availability[id] !== false || done[id]) ids.push(id);
    }
    return ids.slice(0, DAILY_QUEST_SLOT_COUNT);
}

export function isQuestInTodaySlots(questId, availability, date = new Date(), state = null) {
    if (!questId) return false;
    if (questId === 'open_app') return true;
    return getTodayQuestSlotIds(availability, date, state).includes(questId);
}

export function markQuestInState(state, questId, automationRules = {}) {
    const s = normalizeDailyQuestState(state);
    if (s.tasks_done?.[questId]) {
        return { state: s, changed: false, coinReward: 0 };
    }
    const def = DAILY_QUEST_DEFS.find((q) => q.id === questId);
    if (!def) return { state: s, changed: false, coinReward: 0 };

    const next = {
        ...s,
        tasks_done: { ...s.tasks_done, [questId]: new Date().toISOString() },
    };
    const doneCount = countQuestsDone(next);
    if (doneCount >= STREAK_QUESTS_REQUIRED) {
        next.day_marked = true;
        const markedSet = new Set(next.marked_dates || []);
        if (next.today_date) markedSet.add(next.today_date);
        next.marked_dates = [...markedSet].sort();
    }

    let coinReward = 0;
    const awarded = new Set(next.quest_coins_awarded || []);
    const coinAmount = getQuestCoinsFromRules(questId, automationRules);
    if (coinAmount > 0 && !awarded.has(questId)) {
        coinReward = coinAmount;
        next.quest_coins_awarded = [...(next.quest_coins_awarded || []), questId];
    }

    return { state: next, changed: true, coinReward };
}

export function armStreakFreezeInState(state) {
    const s = normalizeDailyQuestState(state);
    if (s.streak_freeze_armed) {
        return { state: s, changed: false, error: 'already_armed' };
    }
    return {
        state: {
            ...s,
            streak_freeze_armed: true,
            streak_freeze_armed_on: getTodayDateStr(),
        },
        changed: true,
        error: null,
    };
}

export function buildQuestTaskList(state, availability, automationRules = {}, date = new Date()) {
    const slotIds = getTodayQuestSlotIds(availability, date, state);
    return slotIds.map((id) => {
        const def = DAILY_QUEST_DEFS.find((q) => q.id === id);
        if (!def) return null;
        const done = !!state.tasks_done?.[def.id];
        return {
            ...def,
            coins: getQuestCoinsFromRules(def.id, automationRules),
            done,
            available: true,
            locked: false,
            lockedHint: null,
        };
    }).filter(Boolean);
}

export function sectorsFromQuestCount(completedCount) {
    return Array.from({ length: STREAK_SECTOR_COUNT }, (_, i) => i < Math.min(completedCount, STREAK_SECTOR_COUNT));
}

export function questIdForWordGameMode(mode) {
    if (TRAINING_MODES.has(mode)) return 'training';
    if (LOOKING_BACK_MODES.has(mode)) return 'looking_back';
    if (PAID_MODE_TO_QUEST[mode]) return PAID_MODE_TO_QUEST[mode];
    return null;
}

export function isPaidQuestId(questId) {
    return PAID_QUEST_IDS.includes(questId);
}
